import React, { useEffect } from 'react'
import { Location } from 'history'
import { Redirect } from 'react-router-dom'
import { isApolloError } from 'apollo-client'

// Constants
import { RouteProps, routes } from 'constants/routes'

// Containers
import { useStripe } from 'containers/StripeProvider'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import { useToast } from 'containers/ToastManager'
import {
  PrivateWorkspaceRouteExtraProps,
  WorkspaceSubscribeSummary,
} from '../../containers'

// Components
import { IndexLayoutError } from 'components/IndexLayout'
import OrderedBreadcrumbs from 'components/OrderedBreadcrumbs'
import SubscriptionDoneModal from 'modules/workspace/modals/SubscriptionDoneModal'
import BillSimulation from 'modules/workspace/components/BillSimulation'

// Utils
import { useScrollTop } from 'utils'
import useWorkspaceSubscribeState from './WorkspaceSubscribeView.hooks'
import { useFlag } from 'constants/flags'
import { useModalContext } from 'containers/ModalManager'
import {
  isBillingFormsStep,
  isPlanSelectorStep,
} from './WorkspaceSubscribeView.utils'

// Pages
import {
  WorkspaceSubscribePlan,
  WorkspaceSubscribePaymentDetails,
} from '../../pages'

// GQL
import {
  useSubscribeToPlan,
  useWorkspaceSubscribeInformation,
} from '../../operations'

// Styles
import {
  BreadcrumbsWrapper,
  IndexLayout,
} from './WorkspaceSubscribeView.styles'

const SUBSCRIBE_CRUMBS = [
  { content: 'Select a plan', step: 'PLAN_SELECT' },
  { content: 'Set Up Payment', step: 'PAYMENT_DETAILS' },
] as const

const SUBSCRIBE_CRUMBS_FOR_BILLING_SIMULATION_FF = (workspaceId: string) =>
  [
    {
      content: 'Set Up Subscription',
      step: 'PLAN_SELECT',
      url: routes.WORKSPACE_SUBSCRIBE.create({
        workspaceId,
      }),
    },
    {
      content: 'Billing Details',
      step: 'PAYMENT_DETAILS',
    },
  ] as const

type Step = typeof SUBSCRIBE_CRUMBS[number]['step']

type WorkspaceSubscribeViewProps = PrivateWorkspaceRouteExtraProps &
  RouteProps<'WORKSPACE_SUBSCRIBE'>

const WorkspaceSubscribeView: React.FC<WorkspaceSubscribeViewProps> = props => {
  const { history, workspace } = props

  // Flags
  // Remove usages of 'onboarding-licenses-signup' feature flag
  // https://github.com/sketch-hq/Cloud/issues/10012
  const isOnboardingLicensesSignUpOn = useFlag('onboarding-licenses-signup')

  // Context
  const { showToast } = useToast()
  const { showModal, hideModal } = useModalContext()

  // Scroll top on the page change
  useScrollTop()

  // Refining the location props to match the expect values
  const location = props.location as Location<{ step?: Step }>
  const workspaceId = workspace.identifier

  // We already made sure the `workspace.customer` exists in "WorkspaceSubscribeViewGuard"
  const customerId = workspace.customer!.identifier

  const [state, triggers] = useWorkspaceSubscribeState()
  const { status: stripeStatus, load, stripe, elements } = useStripe()

  const [subscribe] = useSubscribeToPlan(workspaceId, stripe, elements, {
    onError: error => {
      if (isApolloError(error)) {
        const nonFieldErrors = error.graphQLErrors.filter(
          error => !error.extensions?.stripeErrorCode
        )

        if (nonFieldErrors.length > 0) {
          /* Show a toast warning when there are generic fields that need reporting */
          const nonFieldErrorMessage = nonFieldErrors
            .map(error => error.message)
            .join('; ')

          showToast(nonFieldErrorMessage, 'negative')
        } else {
          /* Show a toast warning when there errors in fields reported by the BE */
          showToast(
            'There were some issues when submitting the form, please review the fields',
            'negative'
          )
        }
      } else {
        showToast(error.message, 'negative')
      }
    },
    onComplete: () => {
      if (isOnboardingLicensesSignUpOn) {
        showModal(SubscriptionDoneModal, {
          onHide: hideModal,
        })
      } else {
        showToast('Your Workspace has been successfully subscribed!')
      }

      history.push(
        routes.WORKSPACE_SETTINGS_GENERAL.create({
          workspaceId,
        })
      )
    },
  })

  const { loading, error, data } = useWorkspaceSubscribeInformation(
    workspaceId,
    customerId
  )

  const isWorkspaceAlreadySubscribed =
    data?.customer && data.customer.paymentDetails.type !== 'NONE'

  useEffect(() => {
    const yearlyPlan = data?.availablePlans?.find(
      ({ type }) => type === 'YEARLY'
    )
    !state.dirty && yearlyPlan && triggers.addSelectedPlan(yearlyPlan)
  }, [data, triggers, state.dirty])

  useEffect(() => {
    if (!state.dirty && isWorkspaceAlreadySubscribed) {
      showToast('Your workspace is already subscribed', 'negative')
    }
  }, [isWorkspaceAlreadySubscribed, showToast, state.dirty])

  // Customer
  const customer = data?.workspace?.customer

  useEffect(() => {
    const numberOfScheduledSeats = customer?.billing?.seats.scheduledSeatsTotal
    const numberOfCurrentSeats = customer?.billing?.seats.currentSeatsTotal
    const numberOfEditors = numberOfScheduledSeats || numberOfCurrentSeats

    if (!state.editors && numberOfEditors) {
      triggers.setEditors(numberOfEditors)
    }
  }, [data, triggers, state.editors, customer])

  const currentStep = location.state?.step || 'PLAN_SELECT'

  /**
   * Reset the user url state if there is no saved state but the "currentStep"
   * is not the "PLAN_SELECT" one.
   *
   * This should be the first check to be done, because the state may become dirty
   * by the automatic select from the yearly plan.
   */
  if (currentStep !== 'PLAN_SELECT' && !state.dirty) {
    return (
      <Redirect
        to={routes.WORKSPACE_SUBSCRIBE.create({
          workspaceId,
        })}
      />
    )
  }

  if (loading) {
    return <DynamicLoadingPage />
  }

  if (error || !data?.customer || !data?.workspace || !data?.availablePlans) {
    return <IndexLayoutError variant="green" />
  }

  /**
   * We redirect the users back to the settings when they are already
   * Subscribed
   */
  if (isWorkspaceAlreadySubscribed && !state.dirty) {
    return <Redirect to={routes.WORKSPACE_SETTINGS.create({ workspaceId })} />
  }

  const currentCrumb = SUBSCRIBE_CRUMBS.findIndex(
    ({ step }) => step === currentStep
  )

  const { memberships } = data.workspace
  const { availablePlans: plans } = data

  const numberOfUsedSeats = customer?.billing?.seats.usedSeats || 0
  const numberOfExtraSeats = customer?.billing?.seats.availableSeats || 0
  const numberOfViewers = memberships.list!.meta.totalCount - numberOfUsedSeats

  return (
    <IndexLayout
      expand={isOnboardingLicensesSignUpOn || currentStep === 'PAYMENT_DETAILS'}
      variant="green"
      headerLink="workspace-settings">
      <BreadcrumbsWrapper isOnboardingLicensesSignUpOn>
        <OrderedBreadcrumbs
          showAllInMobile
          crumbs={
            isOnboardingLicensesSignUpOn
              ? [...SUBSCRIBE_CRUMBS_FOR_BILLING_SIMULATION_FF(workspaceId)]
              : [...SUBSCRIBE_CRUMBS]
          }
          currentCrumb={currentCrumb}
        />
      </BreadcrumbsWrapper>
      {isPlanSelectorStep(currentStep, state) && state.plan && customer && (
        <WorkspaceSubscribePlan
          customer={customer}
          plans={plans}
          selectedPlan={state.plan}
          onPlanChange={triggers.addSelectedPlan}
          onPaymentDetails={async () => {
            if (stripeStatus !== 'success') {
              await load()
            }

            const newLocationState = {
              ...location.state,
              step: 'PAYMENT_DETAILS',
            }
            history.push({ ...location, state: newLocationState })
          }}
          onValidDiscountCode={triggers.applyDiscountCode}
          onClearDiscountCode={triggers.clearDiscountCode}
          discountCode={state.discountCode}
          numberEditors={state.editors || 1}
          onEditorsChange={triggers.setEditors}
        />
      )}
      {isBillingFormsStep(currentStep, state) &&
        customer &&
        state.editors !== undefined && (
          <WorkspaceSubscribePaymentDetails
            onSubmit={async ({ tos, ...billing }) => {
              triggers.setBillingInformation(billing)

              const currentState = {
                ...state,
                billing,
              }

              await subscribe(customer.identifier, currentState, tos)
            }}
            summaryUi={
              isOnboardingLicensesSignUpOn ? (
                <BillSimulation
                  plan={state.plan}
                  trialEnd={customer.billing!.trialEnd!}
                  numberEditors={state.editors}
                  numberExtraSeats={numberOfExtraSeats}
                  discountCode={state.discountCode}
                />
              ) : (
                <WorkspaceSubscribeSummary
                  plan={state.plan}
                  trialEnd={customer.billing!.trialEnd!}
                  numberEditors={state.editors}
                  numberExtraSeats={numberOfExtraSeats}
                  numberViewers={numberOfViewers}
                  storedDiscountCode={state.discountCode}
                  onValidDiscountCode={triggers.applyDiscountCode}
                  onClearDiscountCode={triggers.clearDiscountCode}
                />
              )
            }
          />
        )}
    </IndexLayout>
  )
}

const WorkspaceSubscribeViewGuard: React.FC<WorkspaceSubscribeViewProps> = props => {
  const { workspace } = props
  const { showToast } = useToast()

  useEffect(() => {
    if (!workspace.customer) {
      showToast(
        'You need to upgrade to a Workspace before you can subscribe',
        'negative'
      )
    }
  }, [showToast, workspace.customer])

  /**
   * In the case this workspace is a "Personal Workspace", we shouldn't
   * allow it to subscribe to any plan. It should firstly:
   *
   * - Be migrated to a Workspace
   * - Subscribe to a plan
   */
  if (!workspace.customer) {
    return (
      <Redirect
        to={routes.WORKSPACE_SHARES.create({
          workspaceId: workspace.identifier,
        })}
      />
    )
  }

  return <WorkspaceSubscribeView {...props} />
}

export default WorkspaceSubscribeViewGuard
